import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Input, Button, Card, Label, ErrorText, Select, Heading, Text, Alert } from '../ui';
import { useCreatePlantSource, useUpdatePlantSource } from '../../hooks/usePlantSourceMutations';
import { PLANT_SOURCE_TYPES, PlantSource } from '../../types/plantSource';

const addPlantSourceSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name too long'),
  type: z.enum([
    'NURSERY',
    'SEED_COMPANY',
    'ONLINE_RETAILER',
    'LOCAL_GARDEN_CENTER',
    'PERSONAL_COLLECTION',
    'OTHER',
  ] as const),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
});

type AddPlantSourceFormData = z.infer<typeof addPlantSourceSchema>;

interface AddPlantSourceFormProps {
  source?: PlantSource | null;
  onSuccess?: (sourceId: string) => void;
  onCancel?: () => void;
}

export function AddPlantSourceForm({ source, onSuccess, onCancel }: AddPlantSourceFormProps) {
  const navigate = useNavigate();
  const createPlantSourceMutation = useCreatePlantSource();
  const updatePlantSourceMutation = useUpdatePlantSource();

  const isEditing = !!source;

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
  } = useForm<AddPlantSourceFormData>({
    resolver: zodResolver(addPlantSourceSchema),
    defaultValues: {
      name: source?.name || '',
      type: source?.type || 'NURSERY',
      website: source?.website || '',
    },
  });

  const onSubmit = async (data: AddPlantSourceFormData) => {
    const plantSourceData = {
      name: data.name,
      type: data.type,
      website: data.website || null,
    };

    if (isEditing && source) {
      const updatedSource = await updatePlantSourceMutation.mutateAsync({
        id: source.id,
        updates: plantSourceData,
      });
      if (onSuccess) {
        onSuccess(updatedSource.id);
      }
    } else {
      const newSource = await createPlantSourceMutation.mutateAsync(plantSourceData);
      if (onSuccess) {
        onSuccess(newSource.id);
      } else {
        navigate('/sources');
      }
    }
  };

  return (
    <Card variant="elevated">
      <div className="mb-6">
        <Heading as="h1" size="lg">
          {isEditing ? 'Edit Plant Source' : 'Add New Plant Source'}
        </Heading>
        <Text variant="muted" className="mt-1">
          {isEditing
            ? `Update the details for "${source?.name}"`
            : 'Add a new plant source to track where your plants come from.'}
        </Text>
      </div>

      <form className="space-y-6">
        <div>
          <Label htmlFor="name" required>
            Source Name
          </Label>
          <Input
            id="name"
            type="text"
            placeholder="e.g., Johnny's Selected Seeds"
            {...register('name')}
            error={errors.name?.message}
          />
        </div>

        <div>
          <Label htmlFor="type" required>
            Source Type
          </Label>
          <Select id="type" {...register('type')}>
            {Object.entries(PLANT_SOURCE_TYPES).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          {errors.type && <ErrorText>{errors.type.message}</ErrorText>}
        </div>

        <div>
          <Label htmlFor="website">Website (Optional)</Label>
          <Input
            id="website"
            type="url"
            placeholder="https://example.com"
            {...register('website')}
            error={errors.website?.message}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            isLoading={createPlantSourceMutation.isPending || updatePlantSourceMutation.isPending}
            className="flex-1"
            onClick={async e => {
              e.preventDefault();
              const isValid = await trigger();
              if (isValid) {
                handleSubmit(onSubmit)();
              }
            }}
          >
            {createPlantSourceMutation.isPending || updatePlantSourceMutation.isPending
              ? isEditing
                ? 'Updating...'
                : 'Creating...'
              : isEditing
                ? 'Update Plant Source'
                : 'Create Plant Source'}
          </Button>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </form>

      {(createPlantSourceMutation.isError || updatePlantSourceMutation.isError) && (
        <Alert variant="error" className="mt-6">
          {(createPlantSourceMutation.error || updatePlantSourceMutation.error) instanceof Error
            ? createPlantSourceMutation.error?.message || updatePlantSourceMutation.error?.message
            : `Failed to ${isEditing ? 'update' : 'create'} plant source`}
        </Alert>
      )}
    </Card>
  );
}
